import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, X, Wand2, MessageSquare, Mail, Phone, Share2, Users, Building, Palette, Loader, AlertCircle } from 'lucide-react';
import { templateService } from '../../../services/templateService';
import { isOpenAIAvailable } from '../../../services/openai';

const AIGenerationPanel = ({ onClose, onGenerated }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'email',
    category: 'email_campaigns',
    prompt: '',
    tone: 'professional',
    industry: '',
    targetAudience: '',
    language: 'en',
    tags: []
  });

  const [generating, setGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState('');
  const [error, setError] = useState('');
  const [newTag, setNewTag] = useState('');

  const categories = [
    { value: 'email_campaigns', label: 'Email Campaigns', icon: <Mail className="h-4 w-4" /> },
    { value: 'sms_messages', label: 'SMS Messages', icon: <MessageSquare className="h-4 w-4" /> },
    { value: 'social_media_posts', label: 'Social Media Posts', icon: <Share2 className="h-4 w-4" /> },
    { value: 'sales_scripts', label: 'Sales Scripts', icon: <Phone className="h-4 w-4" /> },
    { value: 'lead_outreach', label: 'Lead Outreach', icon: <Users className="h-4 w-4" /> },
    { value: 'follow_up_sequences', label: 'Follow-up Sequences', icon: <MessageSquare className="h-4 w-4" /> }
  ];

  const tones = [
    { value: 'professional', label: 'Professional', desc: 'Formal and business-appropriate' },
    { value: 'friendly', label: 'Friendly', desc: 'Warm and approachable' },
    { value: 'casual', label: 'Casual', desc: 'Relaxed and conversational' },
    { value: 'enthusiastic', label: 'Enthusiastic', desc: 'Energetic and exciting' },
    { value: 'authoritative', label: 'Authoritative', desc: 'Expert and confident' },
    { value: 'empathetic', label: 'Empathetic', desc: 'Understanding and supportive' },
    { value: 'persuasive', label: 'Persuasive', desc: 'Compelling and influential' },
    { value: 'consultative', label: 'Consultative', desc: 'Advisory and helpful' }
  ];

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Real Estate', 'Education', 'Retail',
    'Manufacturing', 'Consulting', 'Marketing', 'Legal', 'Non-profit', 'Government',
    'Entertainment', 'Automotive', 'Energy', 'Telecommunications', 'Other'
  ];

  const promptTemplates = {
    email_campaigns: [
      'Create a cold email for B2B lead generation',
      'Write a welcome email for new subscribers',
      'Generate a follow-up email for sales prospects',
      'Craft a re-engagement email for inactive leads'
    ],
    sms_messages: [
      'Write a follow-up SMS for meeting reminders',
      'Create an appointment confirmation message',
      'Generate a promotional SMS for special offers',
      'Craft a thank you message for new customers'
    ],
    social_media_posts: [
      'Create an engaging LinkedIn post about industry trends',
      'Write a thought-leadership post for company expertise',
      'Generate a customer success story post',
      'Craft a behind-the-scenes company culture post'
    ],
    sales_scripts: [
      'Create a discovery call script for new prospects',
      'Write a demo presentation script',
      'Generate objection handling responses',
      'Craft a closing conversation script'
    ],
    lead_outreach: [
      'Write a personalized LinkedIn connection request',
      'Create a cold outreach message for prospects',
      'Generate a referral request message',
      'Craft a networking event follow-up message'
    ]
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleAddTag = () => {
    if (newTag?.trim() && !formData?.tags?.includes(newTag?.trim()?.toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev?.tags, newTag?.trim()?.toLowerCase()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev?.tags?.filter(tag => tag !== tagToRemove)
    }));
  };

  const handlePromptTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      prompt: template
    }));
  };

  const handleGenerate = async () => {
    if (!formData?.prompt?.trim()) {
      setError('Please provide a description of what you want to generate.');
      return;
    }

    if (!isOpenAIAvailable()) {
      setError('OpenAI API is not configured. Please check your API key settings.');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      setGenerationStep('Analyzing your requirements...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      setGenerationStep('Generating content with AI...');
      await new Promise(resolve => setTimeout(resolve, 1500));

      setGenerationStep('Optimizing for your audience...');
      
      const result = await templateService?.generateAITemplate({
        name: formData?.name || 'AI Generated Template',
        type: formData?.type,
        category: formData?.category,
        prompt: formData?.prompt,
        tone: formData?.tone,
        industry: formData?.industry,
        targetAudience: formData?.targetAudience,
        tags: [...formData?.tags, 'ai-generated']
      });

      setGenerationStep('Template created successfully!');
      await new Promise(resolve => setTimeout(resolve, 1000));

      onGenerated?.(result);
      
    } catch (err) {
      console.error('Error generating template:', err);
      setError(err?.message || 'Failed to generate template. Please try again.');
      setGenerationStep('');
    } finally {
      setGenerating(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'sms': return <MessageSquare className="h-4 w-4" />;
      case 'call': return <Phone className="h-4 w-4" />;
      case 'social_media': return <Share2 className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e?.target === e?.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">AI Template Generator</h2>
              <p className="text-sm text-gray-600">Create powerful communication templates with AI</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Generation Status */}
          {generating && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200"
            >
              <div className="flex items-center gap-3">
                <Loader className="h-5 w-5 text-blue-600 animate-spin" />
                <div>
                  <div className="font-medium text-blue-900">Generating your template...</div>
                  <div className="text-sm text-blue-700">{generationStep}</div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Error Display */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200 flex items-start gap-3"
            >
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium text-red-900">Generation Failed</div>
                <div className="text-sm text-red-700">{error}</div>
              </div>
            </motion.div>
          )}

          <div className="space-y-6">
            {/* Template Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template Name (Optional)
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Leave blank for auto-generated name"
                disabled={generating}
              />
            </div>

            {/* Type and Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Template Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'email', label: 'Email', icon: <Mail className="h-4 w-4" /> },
                    { value: 'sms', label: 'SMS', icon: <MessageSquare className="h-4 w-4" /> },
                    { value: 'call', label: 'Call Script', icon: <Phone className="h-4 w-4" /> },
                    { value: 'social_media', label: 'Social', icon: <Share2 className="h-4 w-4" /> }
                  ]?.map((type) => (
                    <button
                      key={type?.value}
                      onClick={() => handleInputChange('type', type?.value)}
                      disabled={generating}
                      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        formData?.type === type?.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-gray-200 hover:border-gray-300 text-gray-600'
                      } ${generating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {type?.icon}
                      <span className="text-sm font-medium">{type?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Category
                </label>
                <div className="space-y-2">
                  {categories?.map((category) => (
                    <button
                      key={category?.value}
                      onClick={() => handleInputChange('category', category?.value)}
                      disabled={generating}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        formData?.category === category?.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-gray-200 hover:border-gray-300 text-gray-600'
                      } ${generating ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {category?.icon}
                      <span className="text-sm font-medium">{category?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content Generation Prompt */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What do you want to generate? *
              </label>
              
              {/* Quick Templates */}
              <div className="mb-3">
                <div className="text-xs font-medium text-gray-600 mb-2">Quick Templates:</div>
                <div className="flex flex-wrap gap-2">
                  {promptTemplates?.[formData?.category]?.map((template, index) => (
                    <button
                      key={index}
                      onClick={() => handlePromptTemplate(template)}
                      disabled={generating}
                      className={`text-xs px-3 py-1.5 rounded-full border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-600 hover:text-blue-700 transition-colors ${
                        generating ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {template}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                value={formData?.prompt}
                onChange={(e) => handleInputChange('prompt', e?.target?.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
                placeholder="Describe what you want to generate. Be specific about the purpose, audience, and key messages you want to include..."
                disabled={generating}
              />
            </div>

            {/* Tone Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Palette className="h-4 w-4 inline mr-1" />
                Tone & Style
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {tones?.map((tone) => (
                  <button
                    key={tone?.value}
                    onClick={() => handleInputChange('tone', tone?.value)}
                    disabled={generating}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      formData?.tone === tone?.value
                        ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    } ${generating ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="font-medium text-sm text-gray-900">{tone?.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{tone?.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Industry and Target Audience */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="h-4 w-4 inline mr-1" />
                  Industry
                </label>
                <select
                  value={formData?.industry}
                  onChange={(e) => handleInputChange('industry', e?.target?.value)}
                  disabled={generating}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    generating ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">Select industry...</option>
                  {industries?.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Users className="h-4 w-4 inline mr-1" />
                  Target Audience
                </label>
                <input
                  type="text"
                  value={formData?.targetAudience}
                  onChange={(e) => handleInputChange('targetAudience', e?.target?.value)}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    generating ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  placeholder="e.g., B2B decision makers, potential customers"
                  disabled={generating}
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags (Optional)
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleAddTag()}
                  className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    generating ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  placeholder="Add tags..."
                  disabled={generating}
                />
                <button
                  onClick={handleAddTag}
                  disabled={generating || !newTag?.trim()}
                  className={`px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors ${
                    generating || !newTag?.trim() ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData?.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      disabled={generating}
                      className={`ml-1 text-gray-400 hover:text-gray-600 ${
                        generating ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={generating}
            className={`px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors ${
              generating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || !formData?.prompt?.trim()}
            className={`px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 ${
              generating || !formData?.prompt?.trim() ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {generating ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" />
                Generate Template
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AIGenerationPanel;